import { useState } from 'react'
import type { SearchBy } from '../api/bol'
import { Button, Card } from './ui'

/**
 * Manual unit lookup, used when the sender can't be resolved automatically.
 *
 * Mirrors the fields of bol-ui's UnitSearch, which is built for the full-width BOL3
 * workbench and is too large for a ~350px pane.
 */

const OPTIONS: { value: SearchBy; label: string; placeholder: string }[] = [
    { value: 'address', label: 'Address', placeholder: 'e.g. Abbey Rd, Salisbury or SP1' },
    { value: 'name', label: 'Name', placeholder: 'e.g. Smith' },
    { value: 'email', label: 'Email', placeholder: 'e.g. someone@example.com' },
    { value: 'telephone', label: 'Telephone', placeholder: 'e.g. 01179 846 494 or 494' },
    { value: 'tenant_reference', label: 'Tenant reference', placeholder: 'e.g. T98904' },
    { value: 'all', label: 'All searches', placeholder: 'e.g. Smith, 494, SP1, T98904' },
]

export function ManualSearch({
    initialSearchBy = 'address',
    onSearch,
}: {
    initialSearchBy?: SearchBy
    onSearch: (searchBy: SearchBy, filterValue: string) => void
}) {
    const [searchBy, setSearchBy] = useState<SearchBy>(initialSearchBy)
    const [term, setTerm] = useState('')

    const option = OPTIONS.find((entry) => entry.value === searchBy) ?? OPTIONS[0]!
    const canSubmit = term.trim().length > 0

    return (
        <Card>
            <div className="border-b border-border px-2.5 py-2 text-[11.5px] font-semibold">
                Find the unit manually
            </div>
            <form
                className="px-2.5 py-2"
                onSubmit={(event) => {
                    event.preventDefault()
                    if (canSubmit) onSearch(searchBy, term)
                }}
            >
                <div className="mb-2">
                    <label htmlFor="search-by" className="mb-1 block text-[10.5px] font-semibold text-foreground/55">
                        Search by
                    </label>
                    <select
                        id="search-by"
                        value={searchBy}
                        onChange={(event) => setSearchBy(event.target.value as SearchBy)}
                        className="w-full rounded border border-border bg-white px-2 py-1.5 text-[11.5px]"
                    >
                        {OPTIONS.map((entry) => (
                            <option key={entry.value} value={entry.value}>
                                {entry.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="mb-2">
                    <label htmlFor="search-term" className="mb-1 block text-[10.5px] font-semibold text-foreground/55">
                        Search term
                    </label>
                    <input
                        id="search-term"
                        value={term}
                        onChange={(event) => setTerm(event.target.value)}
                        placeholder={option.placeholder}
                        autoComplete="off"
                        className="w-full rounded border border-border bg-white px-2 py-1.5 text-[11.5px]"
                    />
                </div>

                <Button type="submit" disabled={!canSubmit}>
                    Search
                </Button>
            </form>
        </Card>
    )
}
